import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { DatePicker, DatePickerValue } from '@bloomays-lib/ui.shared';
import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import { useActiveTams } from '../../hooks/useActiveTams';
import { useCLevelAnnualSynthesis } from '../../hooks/useCLevelAnnualSynthesis';
import type { CLevelSynthesisViewMode } from '../../types/primes';
import { FIRST_MARGIN_YEAR, isYearConsultable } from '../../utils/clevelPrimesDashboard.utils';
import { referenceDateToYear, yearToReferenceDate } from '../../utils/annualPrime.utils';
import CLevelPrimesDashboardTable from '../molecules/CLevelPrimesDashboardTable';
import CLevelSynthesisViewTabs from '../molecules/CLevelSynthesisViewTabs';

const CLevelPrimesDashboard = () => {
  const { t } = useTranslation(['primes']);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(() => format(new Date(), 'yyyy'));
  const [synthesisViewMode, setSynthesisViewMode] = useState<CLevelSynthesisViewMode>('marge');

  const { activeTams, loading, error } = useActiveTams({
    fetchPolicy: 'cache-and-network',
    onError: (queryError) => {
      errorLogger(queryError, { extraInfos: 'getActiveTams failed on /primes/dashboard' });
    },
  });
  const synthesisRows = useCLevelAnnualSynthesis(activeTams, selectedYear);
  const yearConsultable = isYearConsultable(selectedYear);
  const tableLoading = loading || (yearConsultable && synthesisRows.some((row) => row.rowStatus === 'loading'));

  useEffect(() => {
    const state = location.state as { primesDashboardToastKey?: string } | null;
    const toastKey = state?.primesDashboardToastKey;
    if (!toastKey) {
      return;
    }
    notify('warning', t(toastKey), undefined, {
      hideProgressBar: true,
      toastId: 'clevel-detail-redirect',
    });
    navigate('.', { replace: true, state: {} });
  }, [location.state, navigate, t]);

  const handleYearChange = (value: DatePickerValue) => {
    if (!value) {
      return;
    }
    const nextYear = referenceDateToYear(value);
    if (!isYearConsultable(nextYear)) {
      const message =
        Number(nextYear) < FIRST_MARGIN_YEAR ? t('historicalDataUnavailable') : t('futureYearUnavailable');
      notify('warning', message, undefined, {
        hideProgressBar: true,
        toastId: 'clevel-invalid-year',
      });
    }
    setSelectedYear(nextYear);
  };

  if (error) {
    return <Message variant="body1">{t('loadingTamsError')}</Message>;
  }

  if (!loading && activeTams.length === 0) {
    return <Message variant="body1">{t('emptyTams')}</Message>;
  }

  return (
    <Container>
      <YearPickerContainer>
        <DatePicker
          views={['year']}
          openTo="year"
          value={yearToReferenceDate(selectedYear)}
          handleChange={handleYearChange}
        />
      </YearPickerContainer>

      <CLevelSynthesisViewTabs value={synthesisViewMode} onChange={setSynthesisViewMode} />

      <CLevelPrimesDashboardTable
        rows={synthesisRows}
        loading={tableLoading}
        selectedYear={selectedYear}
        viewMode={synthesisViewMode}
      />
    </Container>
  );
};

export default CLevelPrimesDashboard;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

const YearPickerContainer = styled('div')(() => ({
  maxWidth: '240px',
}));

const Message = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
}));
